import { useEffect, useState, useMemo } from 'react';
import { Font, usePDF } from '@react-pdf/renderer';
import { Document, Page } from 'react-pdf';
import { useDispatch } from 'react-redux';
import { PURCHASE_ORDER_DOCUMENT_TYPE } from '@received/pricing-model';
import { PaginationBar } from '../../..';
import { getSupplierById } from '../../../../utils/CustomerUtils';
import { images } from '../../../../constants/images';
import { ErrorBoundary } from '../../../../services/ErrorBoundary/ErrorBoundary';
import { getDocumentByType } from './PDFPreview.utils';
import { BillingAccountDetails, Customer } from '../../../../types/customerTypes';
import TWKLausanne from '../../../../fonts/TWKLausanne-450.woff';
import TWKLausanne650 from '../../../../fonts/TWKLausanne-650.woff';
import TWKLausanne850 from '../../../../fonts/TWKLausanne-850.woff';
import { BillingDocumentType } from '../../../../types/generalTypes';
import { Invoice } from '../../../../types/invoiceTypes';
import styles from './PDFPreview.module.scss';

// Type guard to check if document is a billing document (Invoice, CreditNote, Payout)
const isBillingDocument = (doc: BillingDocumentType): doc is Invoice => {
	return doc?.accountingEventType !== PURCHASE_ORDER_DOCUMENT_TYPE;
};

Font.register({
	family: 'TWKLausanne',
	fonts: [
		{
			src: TWKLausanne,
		},
		{
			src: TWKLausanne650,
			fontWeight: 600,
		},
		{
			src: TWKLausanne850,
			fontWeight: 700,
		},
	],
});

Font.registerHyphenationCallback((word) => {
	if (word.length > 13) {
		return word.match(/[\s\S]{1,13}/g) || [];
	}
	return [word];
});

interface PDFPreviewProps {
	document: BillingDocumentType;
	displayRefreshMode?: boolean;
	isOpen?: boolean;
	onRefresh?: () => void;
}

export const PDFPreview = ({ document, displayRefreshMode, isOpen, onRefresh }: PDFPreviewProps) => {
	const dispatch = useDispatch();

	const [bankDetails, setBankDetails] = useState<BillingAccountDetails>();
	const [documentData, setDocumentData] = useState<BillingDocumentType>();
	const [supplier, setSupplier] = useState<Customer>();
	const [{ pageNumber, numPages }, setPageNumbers] = useState<{ pageNumber: number; numPages: number }>({ pageNumber: 1, numPages: 1 });
	const [pageScale, setPageScale] = useState(1);

	const isPurchaseOrder = document?.accountingEventType === PURCHASE_ORDER_DOCUMENT_TYPE;
	const billingDocument = isBillingDocument(document) ? document : undefined;

	// Memoize the PDF document to prevent unnecessary re-renders
	const pdfDocument = useMemo(() => getDocumentByType(documentData || document, supplier, bankDetails), [documentData, supplier, bankDetails]);

	const [instance, updateInstance] = usePDF({
		document: pdfDocument,
	});


	useEffect(() => {
		setDocumentData(document);
	}, [document]);

	useEffect(() => {
		// Only fetch supplier data for billing documents, not purchase orders
		if (!isPurchaseOrder && billingDocument) {
			setSupplierData();
		}
	}, [billingDocument?.billingDetails?.billingAccountId, billingDocument?.supplierId, isPurchaseOrder]);

	useEffect(() => {
		updateDocument();
	}, [documentData, supplier, bankDetails, billingDocument?.usageReportsByProduct]);

	const updateDocument = () => {
		try {
			// For purchase orders, just update if documentData exists
			// For billing documents with pricingModel, check tabs.length
			const shouldUpdate = isPurchaseOrder ? !!documentData : billingDocument?.pricingModel?.tabs?.length;
			shouldUpdate && updateInstance();
		} catch (err) {
			initializePageNumbers();
		}
	};

	const setSupplierData = async () => {
		if (!billingDocument) return;
		const supplier = await getSupplierById(dispatch, billingDocument?.supplierId);
		setSupplier(supplier);
		setBankDetails(
			supplier?.billingDetails?.billingAccounts?.find(
				(account: BillingAccountDetails) => account.id === billingDocument?.billingDetails?.billingAccountId,
			) ?? undefined,
		);
	};

	const onPressRefresh = async () => {
		if (displayRefreshMode) {
			onRefresh?.();
			updateInstance();
			await setSupplierData();
		}
	};

	const goToPreviousPage = () => {
		if (pageNumber !== 1) {
			setPageNumbers((prev) => ({
				...prev,
				pageNumber: prev.pageNumber - 1,
			}));
		}
	};

	const goToNextPage = () => {
		if (pageNumber !== numPages) {
			setPageNumbers((prev) => ({
				...prev,
				pageNumber: prev.pageNumber + 1,
			}));
		}
	};

	const initializePageNumbers = () => {
		setPageNumbers({
			pageNumber: 1,
			numPages: 1,
		});
	};

	const handleZoomIn = () => {
		if (pageScale < 3) {
			setPageScale(pageScale + 0.2);
		}
	};

	const handleZoomOut = () => {
		if (pageScale > 0.3) {
			setPageScale(pageScale - 0.2);
		}
	};

	return (
		<div className={styles.container} onClick={onPressRefresh}>
			<ErrorBoundary
				allowedRetries
				customUIComponent={
					<div className={styles.errorContainer}>
						<img alt='refresh-document-preview' src={images.whiteRefresh} />
					</div>
				}
			>
				<Document
					file={instance?.blob}
					onLoadSuccess={({ numPages }) => {
						setPageNumbers((prev) => ({
							...prev,
							numPages,
						}));
					}}
					onLoadProgress={initializePageNumbers}
					className={styles.pdf}
				>
					{displayRefreshMode && (
						<div className={styles.refreshView}>
							<img alt='refresh-document-preview' src={images.whiteRefresh} className={styles.refreshImage} />
						</div>
					)}

					<Page
						scale={pageScale}
						onLoad={initializePageNumbers}
						onReset={initializePageNumbers}
						pageNumber={pageNumber}
						className={styles.pagePdf}
						renderTextLayer={false}
						renderAnnotationLayer={false}
					/>
				</Document>
			</ErrorBoundary>

			{isOpen && (
				<PaginationBar
					pageNumber={pageNumber}
					numPages={numPages}
					pageScale={pageScale}
					goToPreviousPage={goToPreviousPage}
					goToNextPage={goToNextPage}
					zoomIn={handleZoomIn}
					zoomOut={handleZoomOut}
				/>
			)}
		</div>
	);
};
